<template>
  <div>
    <div v-if="test.daylight_control_enabled">
      <h2 class="bold">{{ $t("tests.functional.daylight_control.title") }}</h2>

      <p class="body1 mt-4">
        {{ $t("tests.functional.daylight_control.question") }}
      </p>
      <v-radio-group v-model="daylight_control">
        <v-radio :label="$t('tests.functional.yes')" value="Yes"></v-radio>
        <v-radio :label="$t('tests.functional.no')" value="No"></v-radio>
      </v-radio-group>

      <div class="caption">{{ $t("tests.functional.comment") }}</div>
      <niko-textarea
        :rows="1"
        :value="daylight_control_comment"
        @onChange="
          (value) => {
            daylight_control_comment = value;
            setFunctionalTestResults(test.functional_test_id);
          }
        "
        :placeholder="$t('tests.functional.comment')"
      />

      <div v-if="test.daylight_zones_enabled">
        <h2 class="bold mt-10">
          {{ $t("tests.functional.daylight_zones.title") }}
        </h2>
        <niko-slider
          class="mt-3"
          :label="$t('tests.functional.daylight_zones.slider')"
          :min="0"
          :max="4"
          step="1"
          :daylightZones="true"
          :value="test.actual_number_of_zones"
          textFieldType="text"
          @onChange="
            (value) =>
              setFunctionalTestZones({
                functional_test_id: test.functional_test_id,
                actual_number_of_zones: value,
              })
          "
        />

        <p class="body1 mt-4">
          {{ $t("tests.functional.daylight_zones.questions.are_dimmed") }}
        </p>
        <v-radio-group v-model="luminaires_zone_responsive">
          <v-radio :label="$t('tests.functional.yes')" value="Yes"></v-radio>
          <v-radio :label="$t('tests.functional.no')" value="No"></v-radio>
        </v-radio-group>

        <div class="caption">{{ $t("tests.functional.comment") }}</div>
        <niko-textarea
          :rows="1"
          :value="luminaires_zone_responsive_comment"
          @onChange="
            (value) => {
              luminaires_zone_responsive_comment = value;
              setFunctionalTestResults(test.functional_test_id);
            }
          "
          :placeholder="$t('tests.functional.comment')"
        />

        <p class="body1 mt-10">
          {{ $t("tests.functional.daylight_zones.questions.can_dim") }}
        </p>
        <v-radio-group v-model="luminaires_zone_switch_off">
          <v-radio :label="$t('tests.functional.yes')" value="Yes"></v-radio>
          <v-radio :label="$t('tests.functional.no')" value="No"></v-radio>
        </v-radio-group>

        <div class="caption">{{ $t("tests.functional.comment") }}</div>

        <niko-textarea
          :rows="1"
          :value="luminaires_zone_switch_off_comment"
          @onChange="
            (value) => {
              luminaires_zone_switch_off_comment = value;
              setFunctionalTestResults(test.functional_test_id);
            }
          "
          :placeholder="$t('tests.functional.comment')"
        />
      </div>
    </div>

    <div v-if="test.motion_detectors_enabled">
      <h2 class="bold mt-10">
        {{ $t("tests.functional.motion_detectors.title") }}
      </h2>

      <div v-if="test.light_on_automatic">
        <p class="body1 mt-4">
          {{ $t("tests.functional.motion_detectors.questions.automatic") }}
        </p>
        <v-radio-group v-model="lights_on_automatically">
          <v-radio :label="$t('tests.functional.yes')" value="Yes"></v-radio>
          <v-radio :label="$t('tests.functional.no')" value="No"></v-radio>
        </v-radio-group>

        <div class="caption">{{ $t("tests.functional.comment") }}</div>
        <niko-textarea
          :rows="1"
          :value="lights_on_automatically_comment"
          @onChange="
            (value) => {
              lights_on_automatically_comment = value;
              setFunctionalTestResults(test.functional_test_id);
            }
          "
          :placeholder="$t('tests.functional.comment')"
        />
      </div>

      <div v-else>
        <p class="body1 mt-4">
          {{ $t("tests.functional.motion_detectors.questions.manual") }}
        </p>
        <v-radio-group v-model="lights_on_by_switch">
          <v-radio :label="$t('tests.functional.yes')" value="Yes"></v-radio>
          <v-radio :label="$t('tests.functional.no')" value="No"></v-radio>
        </v-radio-group>

        <div class="caption">{{ $t("tests.functional.comment") }}</div>
        <niko-textarea
          :rows="1"
          :value="lights_on_by_switch_comment"
          @onChange="
            (value) => {
              lights_on_by_switch_comment = value;
              setFunctionalTestResults(test.functional_test_id);
            }
          "
          :placeholder="$t('tests.functional.comment')"
        />
      </div>
      <p class="body1 mt-10">
        {{ $t("tests.functional.motion_detectors.questions.detectors") }}
      </p>
      <v-radio-group v-model="detector_sensitivity_sufficient">
        <v-radio :label="$t('tests.functional.yes')" value="Yes"></v-radio>
        <v-radio :label="$t('tests.functional.no')" value="No"></v-radio>
      </v-radio-group>

      <div class="caption">{{ $t("tests.functional.comment") }}</div>
      <niko-textarea
        :rows="1"
        :value="detector_sensitivity_sufficient_comment"
        @onChange="
          (value) => {
            detector_sensitivity_sufficient_comment = value;
            setFunctionalTestResults(test.functional_test_id);
          }
        "
        :placeholder="$t('tests.functional.comment')"
      />

      <p class="body1 mt-10">
        {{
          $t("tests.functional.motion_detectors.questions.turn_off", {
            delay: test.light_off_delay,
          })
        }}
      </p>
      <v-radio-group v-model="lights_off_automatically">
        <v-radio :label="$t('tests.functional.yes')" value="Yes"></v-radio>
        <v-radio :label="$t('tests.functional.no')" value="No"></v-radio>
      </v-radio-group>

      <div class="caption">{{ $t("tests.functional.comment") }}</div>
      <niko-textarea
        :rows="1"
        :value="lights_off_automatically_comment"
        @onChange="
          (value) => {
            lights_off_automatically_comment = value;
            setFunctionalTestResults(test.functional_test_id);
          }
        "
        :placeholder="$t('tests.functional.comment')"
      />
    </div>
    <guidance-button page="testing.functional_test" />
  </div>
</template>

<script>
import { mapMutations, mapActions } from "vuex";
import NikoTextarea from "@/components/UI/NikoTextarea";
import NikoSlider from "@/components/UI/NikoSlider";
import GuidanceButton from "@/components/guidance/GuidanceButton";

export default {
  name: "tester-functional-test-results",
  props: ["test"],
  components: {
    NikoSlider,
    NikoTextarea,
    GuidanceButton,
  },
  computed: {
    daylight_control: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "daylight_control"
        );
        return result && result.answer !== undefined && result.answer !== null
          ? result.answer
            ? "Yes"
            : "No"
          : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "daylight_control"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "daylight_control",
            answer: value === "Yes" ? true : false,
            comment: result ? result.comment : "",
          },
        });
        this.setFunctionalTestResults(this.test.functional_test_id);
      },
    },
    daylight_control_comment: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "daylight_control"
        );
        return result ? result.comment : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "daylight_control"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "daylight_control",
            answer:
              result && result.answer !== undefined ? result.answer : null,
            comment: value,
          },
        });
      },
    },
    luminaires_zone_responsive: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "luminaires_zone_responsive"
        );
        return result && result.answer !== undefined && result.answer !== null
          ? result.answer
            ? "Yes"
            : "No"
          : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "luminaires_zone_responsive"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "luminaires_zone_responsive",
            answer: value === "Yes" ? true : false,
            comment: result ? result.comment : "",
          },
        });
        this.setFunctionalTestResults(this.test.functional_test_id);
      },
    },
    luminaires_zone_responsive_comment: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "luminaires_zone_responsive"
        );
        return result ? result.comment : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "luminaires_zone_responsive"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "luminaires_zone_responsive",
            answer:
              result && result.answer !== undefined ? result.answer : null,
            comment: value,
          },
        });
      },
    },
    luminaires_zone_switch_off: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "luminaires_zone_switch_off"
        );
        return result && result.answer !== undefined && result.answer !== null
          ? result.answer
            ? "Yes"
            : "No"
          : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "luminaires_zone_switch_off"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "luminaires_zone_switch_off",
            answer: value === "Yes" ? true : false,
            comment: result ? result.comment : "",
          },
        });
        this.setFunctionalTestResults(this.test.functional_test_id);
      },
    },
    luminaires_zone_switch_off_comment: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "luminaires_zone_switch_off"
        );
        return result ? result.comment : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "luminaires_zone_switch_off"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "luminaires_zone_switch_off",
            answer:
              result && result.answer !== undefined ? result.answer : null,
            comment: value,
          },
        });
      },
    },
    lights_on_automatically: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_on_automatically"
        );
        return result && result.answer !== undefined && result.answer !== null
          ? result.answer
            ? "Yes"
            : "No"
          : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_on_automatically"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "lights_on_automatically",
            answer: value === "Yes" ? true : false,
            comment: result ? result.comment : "",
          },
        });
        this.setFunctionalTestResults(this.test.functional_test_id);
      },
    },
    lights_on_automatically_comment: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_on_automatically"
        );
        return result ? result.comment : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_on_automatically"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "lights_on_automatically",
            answer:
              result && result.answer !== undefined ? result.answer : null,
            comment: value,
          },
        });
      },
    },
    lights_on_by_switch: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_on_by_switch"
        );
        return result && result.answer !== undefined && result.answer !== null
          ? result.answer
            ? "Yes"
            : "No"
          : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_on_by_switch"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "lights_on_by_switch",
            answer: value === "Yes" ? true : false,
            comment: result ? result.comment : "",
          },
        });
        this.setFunctionalTestResults(this.test.functional_test_id);
      },
    },
    lights_on_by_switch_comment: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_on_by_switch"
        );
        return result ? result.comment : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_on_by_switch"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "lights_on_by_switch",
            answer:
              result && result.answer !== undefined ? result.answer : null,
            comment: value,
          },
        });
      },
    },
    detector_sensitivity_sufficient: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "detector_sensitivity_sufficient"
        );
        return result && result.answer !== undefined && result.answer !== null
          ? result.answer
            ? "Yes"
            : "No"
          : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "detector_sensitivity_sufficient"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "detector_sensitivity_sufficient",
            answer: value === "Yes" ? true : false,
            comment: result ? result.comment : "",
          },
        });
        this.setFunctionalTestResults(this.test.functional_test_id);
      },
    },
    detector_sensitivity_sufficient_comment: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "detector_sensitivity_sufficient"
        );
        return result ? result.comment : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) =>
            result.question_identifier === "detector_sensitivity_sufficient"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "detector_sensitivity_sufficient",
            answer:
              result && result.answer !== undefined ? result.answer : null,
            comment: value,
          },
        });
      },
    },
    lights_off_automatically: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_off_automatically"
        );
        return result && result.answer !== undefined && result.answer !== null
          ? result.answer
            ? "Yes"
            : "No"
          : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_off_automatically"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "lights_off_automatically",
            answer: value === "Yes" ? true : false,
            comment: result ? result.comment : "",
          },
        });
        this.setFunctionalTestResults(this.test.functional_test_id);
      },
    },
    lights_off_automatically_comment: {
      get() {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_off_automatically"
        );
        return result ? result.comment : null;
      },
      set(value) {
        const result = this.test.functional_test_results.find(
          (result) => result.question_identifier === "lights_off_automatically"
        );
        this.setFunctionalTestResult({
          functional_test_id: this.test.functional_test_id,
          result: {
            question_identifier: "lights_off_automatically",
            answer:
              result && result.answer !== undefined ? result.answer : null,
            comment: value,
          },
        });
      },
    },
  },
  methods: {
    ...mapMutations("roomStore", ["setFunctionalTestResult"]),
    ...mapActions("projectStore", [
      "setFunctionalTestZones",
      "setFunctionalTestResults",
    ]),
  },
};
</script>
